import { useEffect, useRef } from 'react';
import barsFromLevel from './barsFromLevel';
import { isAgentFloor } from './constants';

const BAR_KEYS = ['--vc-b0', '--vc-b1', '--vc-b2', '--vc-b3', '--vc-b4'];

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Write the vars to the composer root AND to <html>. The device menu's level
// meter lives inside a MUI Popper, which portals to document.body — outside the
// composer subtree — so it only inherits these custom properties from :root.
const docEl = () => (typeof document !== 'undefined' ? document.documentElement : null);

const setVar = (el, name, value) => {
  el?.style.setProperty(name, value);
  docEl()?.style.setProperty(name, value);
};

const clearVars = el => {
  setVar(el, '--vc-level', '0');
  BAR_KEYS.forEach(k => setVar(el, k, '0'));
};

/**
 * Drives the voice-mode CSS custom properties (`--vc-level`, `--vc-b0`..`--vc-b4`)
 * from real audio amplitude, in a single rAF loop, without triggering React
 * re-renders. The bloom, the mic level meter, and the Stop button bars all read
 * these vars via CSS `calc()`.
 *
 * @param {React.RefObject<HTMLElement>} rootRef element the vars are written on
 * @param {object} opts
 * @param {boolean} opts.active   whether voice mode is running
 * @param {() => {user:number, agent:number}} opts.getLevels smoothed 0..1 RMS per side
 * @param {string} opts.phase     current phase (decides which side holds the floor)
 */
export default function useAmplitudeVars(rootRef, { active, getLevels, phase }) {
  const rafRef = useRef(0);
  const smoothRef = useRef(0);
  // Keep the latest phase without restarting the loop.
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  useEffect(() => {
    const el = rootRef.current;
    if (!el || !active) {
      clearVars(el);
      return undefined;
    }

    // Reduced motion: hold a gentle static level, no animation loop.
    if (prefersReducedMotion()) {
      setVar(el, '--vc-level', '0.3');
      BAR_KEYS.forEach(k => setVar(el, k, '0.4'));
      return undefined;
    }

    smoothRef.current = 0;
    const startedAt = performance.now();

    const tick = now => {
      const t = (now - startedAt) / 1000;
      const levels = getLevels ? getLevels() : { user: 0, agent: 0 };
      const agentFloor = isAgentFloor(phaseRef.current);
      const target = agentFloor ? levels.agent : levels.user;
      // Smooth toward the target so bars/bloom don't jitter frame-to-frame.
      smoothRef.current += (target - smoothRef.current) * 0.3;
      const level = smoothRef.current;

      setVar(el, '--vc-level', level.toFixed(3));
      const bars = barsFromLevel(level, t, agentFloor ? 1 : 0.7);
      for (let i = 0; i < BAR_KEYS.length; i += 1) {
        setVar(el, BAR_KEYS[i], bars[i].toFixed(3));
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      clearVars(el);
    };
  }, [active, getLevels, rootRef]);
}
